import React from 'react'
import { Button, Flex, H2, Input, Span, SelectDropdown, Line, EditIcon } from '../../lib/components'
import { Pagination } from '../../lib/components/Pagination'
import { useBreakpoint } from '../../lib/utils/hooks'
import { SM } from '../../lib/utils/constants'
import { GetNewsletterGroupsSearchParams } from '../../lib/types'
import { newsletterGroupSort } from '../../lib/utils/message'
import { getFormattedDate } from '../../lib/utils/helpers'
import './newsletter-groups-list.scss'
import { useNavigate } from 'react-router-dom'

type NewsletterGroupsListPresenterProps = {
  groups: any[]
  isLoading: boolean
  searchParams: GetNewsletterGroupsSearchParams
  handleSearchParams: (params: { name: string; value: string }) => void
  resetSearchParams: () => void
  pagination: {
    total: number
    limit: number
    count: number
    offset: number
  }
  handleCurrentPage: (currentPage: number) => void
  currentPage: number
}

const formattedDate = (date: string) => getFormattedDate(new Date(date), 'D MMM YYYY [at] h:mm A')

function NewsletterGroupsListPresenter({
  groups,
  isLoading,
  searchParams,
  handleSearchParams,
  resetSearchParams,
  pagination,
  handleCurrentPage,
  currentPage,
}: NewsletterGroupsListPresenterProps) {
  const { breakpoint } = useBreakpoint()
  const navigate = useNavigate()

  const handleEditClick = (groupId: string) => {
    navigate(`/newsletter/groups/${groupId}/edit`)
  }

  return (
    <Flex variant='column-center'>
      <H2 classes={['mb-48']} text='Newsletter Groups' />

      <div className='groups-list-toolbar-layout gap-12 mb-16 mt-16'>
        <Input
          name='keyword'
          value={searchParams.keyword || ''}
          maxLength={100}
          handleChange={(e) => handleSearchParams({ name: e.target.name, value: e.target.value })}
          placeholder='Search by group name or description'
        />
        <div className='sort-reset'>
          <SelectDropdown
            name='sortBy'
            id='sortById'
            formId=''
            list={newsletterGroupSort}
            placeholder={'Sort'}
            value={searchParams.sortBy}
            handleChange={(val) => handleSearchParams({ name: 'sortBy', value: val })}
            dataTestId='groups-list-sort-select'
          />
          <Button
            handleClick={() => resetSearchParams()}
            classes={['reset-btn']}
            variant='secondary-btn'
            text='Reset'
          />
        </div>
      </div>

      <Line />

      {isLoading ? (
        <Flex variant={'column-center'} classes={['mt-12']}>
          <Span classes={['center']} variant={'thin'} text='Loading...' />
        </Flex>
      ) : groups && groups.length > 0 ? (
        <>
          <div className='w-100'>
            <Flex
              variant='row'
              classes={['mb-24', 'table_scroll_horizontal']}
              dataTestId='groups-table-container'
            >
              <table className='styled-table'>
                <thead>
                  <tr>
                    <th>Name</th>
                    <th>Description</th>
                    <th>Emails</th>
                    <th>Created At</th>
                    <th>Updated At</th>
                    <th>Edit</th>
                  </tr>
                </thead>
                <tbody>
                  {groups.map((group: any) => (
                    <tr key={group.id}>
                      <td>{group.name || '—'}</td>
                      <td>{group.description || '—'}</td>
                      <td>{group.emails.length}</td>
                      <td>{group.created_at ? formattedDate(group.created_at) : '—'}</td>
                      <td>{group.updated_at ? formattedDate(group.updated_at) : '—'}</td>
                      <td>
                        <EditIcon
                          classes={['align-center', 'pointer']}
                          height='24'
                          width='24'
                          onClick={() => handleEditClick(group.id)}
                          dataTestId='groups-list-edit-icon'
                        />
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </Flex>
          </div>
          {breakpoint && breakpoint === SM ? null : (
            <Pagination
              currentPage={currentPage}
              handleCurrentPage={handleCurrentPage}
              pagination={pagination}
            />
          )}
        </>
      ) : (
        <Flex variant={'column-center'} classes={['mt-12']}>
          <Span classes={['center']} variant={'thin'} text='No Groups Found' />
        </Flex>
      )}
    </Flex>
  )
}

export { NewsletterGroupsListPresenter }
